import { STRATEGY_METRIC_TYPE } from './constants';
import { strategyApi } from './strategyApi';
import { buildSearchParamsAuto } from '../utils';
import type { StrategyMetric, UpdateStrategyMetricParams, DeleteStrategyMetricParams } from './types';

export const strategyMetricsApi = strategyApi.injectEndpoints({
  endpoints: (builder) => ({
    getStrategyMetrics: builder.query<{ results: StrategyMetric[] }, { strategyType: string; strategyId: string; scope?: string }>({
      query: ({ strategyType, strategyId, scope = 'TABLE' }) => {
        const queryParams = buildSearchParamsAuto({ scope });
        return {
          url: `v1/strategies/strategy-metrics/${strategyType}/${strategyId}/metrics/?${queryParams}`,
        };
      },
      providesTags: (result, error, { strategyId }) => [
        { type: STRATEGY_METRIC_TYPE, id: strategyId },
      ],
    }),
    getStrategyMetricDetails: builder.query<StrategyMetric, { metricId: string }>({
      query: ({ metricId }) => ({
        url: `v1/strategies/strategy-metrics/${metricId}/`,
      }),
      providesTags: (result, error, { metricId }) => [
        { type: STRATEGY_METRIC_TYPE, id: metricId },
      ],
    }),
    updateStrategyMetric: builder.mutation<StrategyMetric, UpdateStrategyMetricParams>({
      query: ({ metricId, body }) => ({
        url: `v1/strategies/strategy-metrics/${metricId}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { metricId, strategyId }) => {
        const tags = [{ type: STRATEGY_METRIC_TYPE, id: metricId }];

        // Invalidate the strategy's metrics list using the provided strategyId
        // or fallback to result.strategy_id
        const strategyIdToInvalidate = strategyId || result?.strategy_id;
        if (strategyIdToInvalidate) {
          tags.push({
            type: STRATEGY_METRIC_TYPE,
            id: strategyIdToInvalidate.toString(),
          });
        }

        return tags;
      },
    }),
    deleteStrategyMetric: builder.mutation<{ strategy_id?: number } | void, DeleteStrategyMetricParams>({
      query: ({ metricId }) => ({
        url: `v1/strategies/strategy-metrics/${metricId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { metricId, strategyId }) => {
        const tags = [{ type: STRATEGY_METRIC_TYPE, id: metricId }];

        // Invalidate the strategy's metrics list using the provided strategyId
        // or fallback to result.strategy_id
        const fallbackId = result && typeof result === 'object' ? result.strategy_id : undefined;
        const strategyIdToInvalidate = strategyId || fallbackId;
        if (strategyIdToInvalidate) {
          tags.push({
            type: STRATEGY_METRIC_TYPE,
            id: strategyIdToInvalidate.toString(),
          });
        }

        return tags;
      },
    }),
  }),
});

export const {
  useGetStrategyMetricsQuery,
  useUpdateStrategyMetricMutation,
  useDeleteStrategyMetricMutation,
} = strategyMetricsApi;
